import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wand2, Video, QrCode, Mail, ArrowRight, ExternalLink, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import { setSeoMetadata } from '../lib/seo';

/**
 * Free Tools directory.
 *
 * Tools are listed here as a local registry rather than a CMS collection, for
 * now: there are only a couple, and each is either a code module with its own
 * route (slug) or a link out to a separate piece of software we run
 * (externalUrl), so a database row would describe something already stated
 * either in code or right here. Move this to a collection when adding a tool
 * becomes a content decision rather than a build.
 *
 * `ready: false` marks a tool that is announced but not yet routed. It renders
 * without a link rather than pointing at a 404 — a dead nav item reads as a
 * broken site.
 *
 * A tool is either internal (`slug`, opens at /tools/:slug) or external
 * (`externalUrl`, opens in a new tab) — never both.
 */
const TOOLS = [
  {
    slug: 'background-remover',
    name: 'Background Remover',
    tagline: 'Remove image backgrounds in your browser',
    description:
      'Drop in a photo and get a transparent PNG back. Runs entirely on your own device — the image is never uploaded, because there is no server to upload it to.',
    icon: Wand2,
    points: ['No account needed', 'Nothing is uploaded', 'Unlimited and free'],
    ready: true,
  },
  {
    externalUrl: 'https://meet.saysme.org',
    name: 'Online Meetings',
    tagline: 'Free video calls, no account needed',
    description:
      'Start or join a video call in one click. No downloads, no sign-up, nothing to install — just a link you can share.',
    icon: Video,
    points: ['No account needed', 'Nothing to install', 'Free to use'],
    ready: true,
  },
  {
    slug: 'qr-code',
    name: 'QR Code Generator',
    tagline: 'Turn a link into a QR code',
    description:
      'Create a QR code for any link, then download or print it. Nothing is saved — sign in if you want to keep it, organize it, or make it editable later.',
    icon: QrCode,
    points: ['No account needed', 'Download or print', 'Not saved unless you sign in'],
    ready: true,
  },
  {
    slug: 'email-signatures',
    name: 'Email Signature Builder',
    tagline: 'A professional signature, built for your brand',
    description:
      'Pick a template, drop in your logo, colors, and contact info, then copy it straight into your inbox. Free with any account.',
    icon: Mail,
    points: ['Professional templates', 'Your logo & colors', 'Free account required'],
    ready: true,
  },
];

const FreeToolsPage = () => {
  const { siteName } = useSiteSettings();
  const { portal_qr_codes_enabled } = useSiteFeatureFlags();
  // The QR tool follows the "QR Codes" flag; when it is off its card is not listed.
  const visibleTools = TOOLS.filter((t) => t.slug !== 'qr-code' || portal_qr_codes_enabled === true);

  useEffect(() => {
    setSeoMetadata({
      title: 'Free Tools',
      description:
        'Free browser-based tools — no account, no upload, no catch. Built and maintained by ' +
        (siteName || 'our team') + '.',
      canonicalPath: '/tools',
    });
  }, [siteName]);

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />

      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
            FREE TOOLS
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Useful things, <span className="text-[#FF6B00]">free to use.</span>
          </h1>
          <p className="text-[#dfddd6] text-lg max-w-2xl">
            Small tools we built because we needed them. No account, no upload, no catch &mdash;
            most run entirely in your browser.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleTools.map((tool) => {
              const Icon = tool.icon;
              const isExternal = Boolean(tool.externalUrl);
              const key = tool.slug || tool.externalUrl;
              const testSlug = tool.slug || tool.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

              const card = (
                <article
                  className={`h-full rounded-2xl border bg-[#302f2c] p-6 flex flex-col transition-colors ${
                    tool.ready
                      ? 'border-[#4a3520] hover:border-[#FF6B00] group'
                      : 'border-[#4a3520] opacity-80'
                  }`}
                  data-testid={`tool-card-${testSlug}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FF6B00]/10 text-[#FF6B00]">
                      <Icon className="w-5 h-5" />
                    </span>
                    {!tool.ready && (
                      <span className="inline-flex items-center gap-1 text-[#888680] text-xs font-semibold">
                        <Lock className="w-3 h-3" />
                        COMING SOON
                      </span>
                    )}
                  </div>

                  <h2 className="text-white text-lg font-bold mb-1">{tool.name}</h2>
                  <p className="text-[#FF6B00] text-sm mb-3">{tool.tagline}</p>
                  <p className="text-[#888680] text-sm leading-relaxed mb-4">
                    {tool.description}
                  </p>

                  <ul className="space-y-1.5 mb-5">
                    {tool.points.map((p) => (
                      <li key={p} className="text-[#dfddd6] text-sm flex items-start gap-2">
                        <span className="text-[#FF6B00] mt-0.5">&bull;</span>
                        {p}
                      </li>
                    ))}
                  </ul>

                  {tool.ready && (
                    <div className="mt-auto pt-2">
                      <span className="inline-flex items-center gap-1.5 text-white group-hover:text-[#FF6B00] transition-colors text-sm font-semibold">
                        {isExternal ? 'Open meeting' : 'Open tool'}
                        {isExternal ? (
                          <ExternalLink className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowRight className="w-3.5 h-3.5" />
                        )}
                      </span>
                    </div>
                  )}
                </article>
              );

              if (!tool.ready) {
                // Not routed yet: render without a link rather than a dead one.
                return <div key={key}>{card}</div>;
              }

              // External tools (a separate piece of software we run elsewhere)
              // open in a new tab so visitors never lose their place on this
              // site. Internal tools route within the app as before.
              return isExternal ? (
                <a
                  key={key}
                  href={tool.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  {card}
                </a>
              ) : (
                <Link key={key} to={`/tools/${tool.slug}`} className="block">
                  {card}
                </Link>
              );
            })}
          </div>

          <p className="text-[#888680] text-sm mt-10">
            More on the way. If there is something you keep doing by hand, tell us &mdash; it
            might be worth building.{' '}
            <Link to="/contact" className="text-[#FF6B00] hover:underline">
              Get in touch
            </Link>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FreeToolsPage;
